import { TrendingUp, TrendingDown, Loader2 } from 'lucide-react'
import type { CurrencyCode } from '@klear/constants/currency'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { useSupplierGrowthData } from '@/hooks/use-supplier-growth-data'
import isFinite from 'lodash/isFinite'

type Props = {
  selectedCurrency: CurrencyCode
  traderId: number
}

export function GrowingSuppliersCard({ selectedCurrency, traderId }: Props) {
  const { growingSuppliers, isLoading, error } = useSupplierGrowthData(selectedCurrency, traderId)

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-50 dark:bg-emerald-950/40 rounded-lg">
            <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <CardTitle className="text-base font-semibold">Growing Usage</CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">Last 3 vs. prior 3 months</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {!isLoading && error && <p className="text-sm text-destructive py-4 text-center">Failed to load data</p>}
        {!isLoading && !error && growingSuppliers.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">No growth data available</p>
        )}
        {!isLoading && !error && growingSuppliers.length > 0 && (
          <div className="space-y-2">
            {growingSuppliers.map((supplier) => (
              <div
                key={supplier.name}
                className="flex justify-between items-center p-3 bg-emerald-50 dark:bg-emerald-950/40 rounded-lg border border-emerald-100 dark:border-emerald-900/60"
              >
                <span className="text-sm font-medium text-foreground truncate mr-2">{supplier.name}</span>
                <span className="font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap tabular-nums">
                  +{isFinite(supplier.growthRate) ? `${supplier.growthRate.toFixed(0)}%` : '-'}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function DecliningSuppliersCard({ selectedCurrency, traderId }: Props) {
  const { decliningSuppliers, isLoading, error } = useSupplierGrowthData(selectedCurrency, traderId)

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-rose-50 dark:bg-rose-950/40 rounded-lg">
            <TrendingDown className="w-5 h-5 text-rose-600 dark:text-rose-400" />
          </div>
          <div>
            <CardTitle className="text-base font-semibold">Declining Usage</CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">Last 3 vs. prior 3 months</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {!isLoading && error && <p className="text-sm text-destructive py-4 text-center">Failed to load data</p>}
        {!isLoading && !error && decliningSuppliers.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">No decline data available</p>
        )}
        {!isLoading && !error && decliningSuppliers.length > 0 && (
          <div className="space-y-2">
            {decliningSuppliers.map((supplier) => (
              <div
                key={supplier.name}
                className="flex justify-between items-center p-3 bg-rose-50 dark:bg-rose-950/40 rounded-lg border border-rose-100 dark:border-rose-900/60"
              >
                <span className="text-sm font-medium text-foreground truncate mr-2">{supplier.name}</span>
                <span className="font-bold text-rose-600 dark:text-rose-400 whitespace-nowrap tabular-nums">
                  {isFinite(supplier.growthRate) ? `${supplier.growthRate.toFixed(0)}%` : '-'}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
